import React, { useState, useRef, useCallback, useEffect } from "react"
import { GallerySection } from "@/shared/types/product"
import { useSnackbar } from "@/shared/context/SnackbarContext"
import { generateProductText } from "@/shared/services/products"

interface ReplaceState {
  sectionId: number
  imageIndex: number
}

type EditableGallerySectionField = keyof Omit<
  GallerySection,
  "id" | "images" | "files" | "selectedIndex" | "selectedPromptIds" | "keywords"
>

interface EditorState {
  sectionId: number
  imageIndex: number
}

interface UseGalleryResult {
  sections: GallerySection[]
  fileInputRef: React.RefObject<HTMLInputElement | null>
  replaceFileInputRef: React.RefObject<HTMLInputElement | null>
  editorState: EditorState | null
  generatingTextFor: number | null
  handleUploadClick: () => void
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleDroppedFiles: (files: File[] | FileList) => void
  handleReplaceFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  handleSelectImage: (sectionId: number, index: number) => void
  handleFieldChange: (sectionId: number, field: EditableGallerySectionField, value: string) => void
  handleKeywordsChange: (sectionId: number, keywords: string[]) => void
  handleAITextFill: (sectionId: number, name: string, description: string, keywords: string[]) => void
  handleGenerateText: (sectionId: number) => Promise<void>
  handlePromptToggle: (sectionId: number, promptId: string) => void
  handleRemovePromptFromSections: (promptId: string) => void
  handleOpenEditor: (sectionId: number, imageIndex: number) => void
  handleEditorSave: (base64: string) => void
  handleEditorClose: () => void
  handleReplaceImage: (sectionId: number, imageIndex: number) => void
  handleRemoveSection: (sectionId: number) => void
  resetSections: () => void
}

const useGallery = (maxSelected: number): UseGalleryResult => {
  const [sections, setSections] = useState<GallerySection[]>([])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const replaceFileInputRef = useRef<HTMLInputElement | null>(null)
  const [editorState, setEditorState] = useState<EditorState | null>(null)
  const [replaceState, setReplaceState] = useState<ReplaceState | null>(null)
  const [generatingTextFor, setGeneratingTextFor] = useState<number | null>(null)
  const { showSnackbar } = useSnackbar()
  // Bug 14: use auto-incrementing ref instead of Date.now() to avoid ID collisions
  const nextSectionId = useRef(0)

  useEffect(() => {
    return () => {
      sections.forEach(section => section.images.forEach(url => URL.revokeObjectURL(url)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Object URLs are revoked only on unmount; the dependency array is intentionally empty.
  }, [])

  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = ""
      fileInputRef.current.click()
    }
  }, [])

  const isImageFile = useCallback((file: File): boolean => {
    if (file.type.startsWith("image/")) return true
    return /\.(avif|bmp|gif|heic|heif|jpe?g|png|svg|webp)$/i.test(file.name)
  }, [])

  const appendFilesAsSections = useCallback(
    (incomingFiles: File[], showSkippedWarning: boolean) => {
      if (incomingFiles.length === 0) {
        return
      }

      const imageFiles = incomingFiles.filter(isImageFile)
      const skippedCount = incomingFiles.length - imageFiles.length

      if (imageFiles.length === 0) {
        if (showSkippedWarning) {
          showSnackbar("Only image files can be uploaded.", "warning")
        }
        return
      }

      setSections(prev => {
        const nextSections = imageFiles.map(file => ({
          id: nextSectionId.current++,
          images: [URL.createObjectURL(file)],
          files: [file],
          selectedIndex: 0,
          name: "",
          description: "",
          keywords: [],
          category: "",
          price: "",
          selectedPromptIds: []
        }))

        return [...prev, ...nextSections]
      })

      if (showSkippedWarning && skippedCount > 0) {
        showSnackbar(`${skippedCount} non-image file${skippedCount === 1 ? "" : "s"} skipped during upload.`, "warning")
      }
    },
    [isImageFile, showSnackbar]
  )

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || [])
      appendFilesAsSections(files, false)
    },
    [appendFilesAsSections]
  )

  const handleDroppedFiles = useCallback(
    (files: File[] | FileList) => {
      appendFilesAsSections(Array.from(files), true)
    },
    [appendFilesAsSections]
  )

  const handleSelectImage = useCallback((sectionId: number, index: number) => {
    setSections(prev => prev.map(s => (s.id === sectionId ? { ...s, selectedIndex: index } : s)))
  }, [])

  const handleFieldChange = useCallback((sectionId: number, field: EditableGallerySectionField, value: string) => {
    if (field === "price" && value.startsWith("-")) return
    setSections(prev => prev.map(s => (s.id === sectionId ? { ...s, [field]: value } : s)))
  }, [])

  const handleKeywordsChange = useCallback((sectionId: number, keywords: string[]) => {
    setSections(prev => prev.map(s => (s.id === sectionId ? { ...s, keywords } : s)))
  }, [])

  const handleAITextFill = useCallback((sectionId: number, name: string, description: string, keywords: string[]) => {
    setSections(prev =>
      prev.map(s =>
        s.id === sectionId
          ? {
              ...s,
              name,
              description,
              keywords
            }
          : s
      )
    )
  }, [])

  const handlePromptToggle = useCallback(
    (sectionId: number, promptId: string) => {
      setSections(prev =>
        prev.map(s => {
          if (s.id !== sectionId) return s
          const already = s.selectedPromptIds.includes(promptId)
          if (!already && s.selectedPromptIds.length >= maxSelected) return s
          return {
            ...s,
            selectedPromptIds: already
              ? s.selectedPromptIds.filter(id => id !== promptId)
              : [...s.selectedPromptIds, promptId]
          }
        })
      )
    },
    [maxSelected]
  )

  const handleRemovePromptFromSections = useCallback((promptId: string) => {
    setSections(prev =>
      prev.map(section => ({
        ...section,
        selectedPromptIds: section.selectedPromptIds.filter(id => id !== promptId)
      }))
    )
  }, [])

  const handleGenerateText = useCallback(
    async (sectionId: number) => {
      if (generatingTextFor !== null) {
        return
      }

      const section = sections.find(item => item.id === sectionId)
      const selectedFile = section?.files[section.selectedIndex]

      if (!selectedFile) {
        showSnackbar("Select an image before generating product text.", "warning")
        return
      }

      setGeneratingTextFor(sectionId)

      try {
        const result = await generateProductText(selectedFile)
        handleAITextFill(sectionId, result.name, result.description, result.keywords)
        showSnackbar("Product text generated successfully!", "success")
      } catch (error: any) {
        console.error("Error generating product text:", error)
        const message =
          error?.code === "functions/resource-exhausted"
            ? "The selected image is too large for AI text generation. Try a smaller image."
            : "Failed to generate product text. Please try again."
        showSnackbar(message, "error")
      } finally {
        setGeneratingTextFor(current => (current === sectionId ? null : current))
      }
    },
    [generatingTextFor, sections, handleAITextFill, showSnackbar]
  )

  const handleOpenEditor = useCallback((sectionId: number, imageIndex: number) => {
    setEditorState({ sectionId, imageIndex })
  }, [])

  const handleEditorSave = useCallback(
    (base64: string) => {
      if (!editorState) return
      setSections(prev =>
        prev.map(s => {
          if (s.id !== editorState.sectionId) return s
          const oldUrl = s.images[editorState.imageIndex]
          // Revoke the previous object URL if it is a blob URL to prevent memory leak
          if (oldUrl && oldUrl.startsWith("blob:")) {
            URL.revokeObjectURL(oldUrl)
          }
          const updatedImages = s.images.map((img, idx) => (idx === editorState.imageIndex ? base64 : img))
          // Bug 1: also replace the File entry so the upload sends the edited image
          const mimeMatch = base64.match(/^data:(image\/[a-z0-9+-]+);base64,/)
          if (!mimeMatch) {
            console.warn("handleEditorSave: unexpected base64 format; defaulting to image/png")
          }
          const mimeType = mimeMatch ? mimeMatch[1] : "image/png"
          const byteString = atob(base64.split(",")[1])
          const byteArray = new Uint8Array(byteString.length)
          for (let i = 0; i < byteString.length; i++) {
            byteArray[i] = byteString.charCodeAt(i)
          }
          const blob = new Blob([byteArray], { type: mimeType })
          const ext = mimeType.split("/")[1]
          const originalFile = s.files[editorState.imageIndex]
          const editedFile = new File([blob], originalFile?.name ?? `edited.${ext}`, { type: mimeType })
          const updatedFiles = s.files.map((f, idx) => (idx === editorState.imageIndex ? editedFile : f))
          return { ...s, images: updatedImages, files: updatedFiles }
        })
      )
      setEditorState(null)
    },
    [editorState]
  )

  const handleEditorClose = useCallback(() => {
    setEditorState(null)
  }, [])

  const handleReplaceImage = useCallback((sectionId: number, imageIndex: number) => {
    setReplaceState({ sectionId, imageIndex })
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = ""
      replaceFileInputRef.current.click()
    }
  }, [])

  const handleReplaceFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file || !replaceState) return
      const newUrl = URL.createObjectURL(file)
      setSections(prev =>
        prev.map(s => {
          if (s.id !== replaceState.sectionId) return s
          const oldUrl = s.images[replaceState.imageIndex]
          URL.revokeObjectURL(oldUrl)
          const updatedImages = s.images.map((img, idx) => (idx === replaceState.imageIndex ? newUrl : img))
          const updatedFiles = s.files.map((f, idx) => (idx === replaceState.imageIndex ? file : f))
          return { ...s, images: updatedImages, files: updatedFiles }
        })
      )
      setReplaceState(null)
    },
    [replaceState]
  )

  const handleRemoveSection = useCallback((sectionId: number) => {
    setSections(prev =>
      prev.filter(section => {
        if (section.id !== sectionId) return true
        section.images.forEach(url => {
          if (url && url.startsWith("blob:")) {
            URL.revokeObjectURL(url)
          }
        })
        return false
      })
    )
  }, [])

  const resetSections = useCallback(() => {
    setSections(prev => {
      prev.forEach(section =>
        section.images.forEach(url => {
          if (url && url.startsWith("blob:")) {
            URL.revokeObjectURL(url)
          }
        })
      )
      return []
    })
  }, [])

  return {
    sections,
    fileInputRef,
    replaceFileInputRef,
    editorState,
    generatingTextFor,
    handleUploadClick,
    handleFileChange,
    handleDroppedFiles,
    handleReplaceFileChange,
    handleSelectImage,
    handleFieldChange,
    handleKeywordsChange,
    handleAITextFill,
    handleGenerateText,
    handlePromptToggle,
    handleRemovePromptFromSections,
    handleOpenEditor,
    handleEditorSave,
    handleEditorClose,
    handleReplaceImage,
    handleRemoveSection,
    resetSections
  }
}

export { useGallery }
export type { EditorState, EditableGallerySectionField, ReplaceState }
